import React, { useEffect, useState } from "react";
import { X, CreditCard, Zap } from "lucide-react";
import { getPaymentProviders, startCheckout } from "../../lib/paymentProvider";

/**
 * PaymentProviderPicker — small modal that appears AFTER the app order is
 * created. Buyer picks Cashfree or Razorpay; the picker delegates to the
 * matching flow in lib/paymentProvider.
 *
 * If only one provider is live, we skip the modal and auto-start that flow.
 */
export default function PaymentProviderPicker({
  open,
  onClose,
  appOrderId,
  cashfreeSessionId,
  amountInr,
  buyer,
  onSuccess,
  onFailure,
}) {
  const [providers, setProviders] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    getPaymentProviders().then(setProviders);
  }, [open]);

  useEffect(() => {
    // Auto-start when there's only ONE enabled provider — no reason to
    // interrupt the buyer with a picker.
    if (!open || !providers) return;
    const enabled = (providers.providers || []).filter((p) => p.enabled);
    if (enabled.length === 1) {
      const only = enabled[0];
      setBusy(true);
      startCheckout({
        provider: only.key, appOrderId, cashfreeSessionId, buyer,
        onSuccess: (info) => { setBusy(false); onClose?.(); onSuccess?.(info); },
        onFailure: (err) => { setBusy(false); onFailure?.(err); },
      });
    }
  }, [open, providers, appOrderId, cashfreeSessionId, buyer, onSuccess, onFailure, onClose]);

  if (!open) return null;
  const enabled = (providers?.providers || []).filter((p) => p.enabled);
  if (enabled.length === 0 && providers) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" data-testid="payment-picker-none">
        <div className="bg-white rounded-md p-6 max-w-md">
          <div className="font-heading text-lg text-ink mb-2">No payment provider configured</div>
          <div className="text-sm text-ink-soft mb-4">Ask an admin to enable Cashfree or Razorpay before checkout.</div>
          <button onClick={onClose} className="text-xs text-navy hover:underline">Close</button>
        </div>
      </div>
    );
  }
  if (enabled.length <= 1) {
    // The auto-start effect above will handle it. Render a lightweight overlay.
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30" data-testid="payment-picker-loading">
        <div className="bg-white rounded-md px-6 py-4 shadow-lg text-sm text-ink-soft">
          {busy ? "Opening secure checkout…" : "Preparing checkout…"}
        </div>
      </div>
    );
  }

  const trigger = (provider) => {
    setBusy(true);
    startCheckout({
      provider, appOrderId, cashfreeSessionId, buyer,
      onSuccess: (info) => { setBusy(false); onClose?.(); onSuccess?.(info); },
      onFailure: (err) => { setBusy(false); onFailure?.(err); },
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" data-testid="payment-picker">
      <div className="bg-white rounded-md max-w-md w-full m-4 border border-navy/10">
        <div className="flex items-center justify-between px-5 py-3 border-b border-navy/10">
          <div>
            <div className="overline">Choose payment method</div>
            <div className="text-[10px] font-mono text-ink-muted mt-0.5">
              Order {appOrderId} · ₹{Number(amountInr || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <button onClick={onClose} data-testid="payment-picker-close" disabled={busy}>
            <X size={16} className="text-ink-muted hover:text-ink" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {enabled.map((p) => (
            <button
              key={p.key}
              disabled={busy}
              onClick={() => trigger(p.key)}
              className="w-full flex items-center justify-between gap-3 border border-navy/15 hover:border-gold hover:bg-gold/5 rounded px-4 py-3 transition disabled:opacity-50 disabled:cursor-not-allowed text-left"
              data-testid={`payment-picker-choose-${p.key}`}
            >
              <div className="flex items-center gap-3">
                {p.key === "razorpay"
                  ? <Zap size={18} className="text-[#3E5FE8]" />
                  : <CreditCard size={18} className="text-[#1F5B4A]" />}
                <div>
                  <div className="font-heading text-ink">{p.label}</div>
                  <div className="text-[10px] font-mono text-ink-muted">
                    {p.key === "razorpay"
                      ? `UPI · Cards · Netbanking · Wallets${p.mode ? " · " + p.mode : ""}`
                      : "Cards · UPI · Netbanking · Wallets"}
                  </div>
                </div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Pay →</span>
            </button>
          ))}
        </div>
        <div className="px-5 pb-4 text-[10px] font-mono text-ink-muted">
          Secure. GST invoice auto-emailed on success. VenturaZ never stores card details.
        </div>
      </div>
    </div>
  );
}
